import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import {
  CustomToolbarItemModel,
  DocumentEditorContainerComponent,
  DocumentEditorSettingsModel
} from '@syncfusion/ej2-angular-documenteditor';
import {ClickEventArgs, NodeSelectEventArgs, TreeViewComponent} from '@syncfusion/ej2-angular-navigations';
import {L10n} from '@syncfusion/ej2-base';
import {DataManager, Predicate, Query} from '@syncfusion/ej2-data';
import {FieldsSettingsModel} from '@syncfusion/ej2-navigations/src/treeview/treeview-model';

import cloneDeep from 'lodash-es/cloneDeep';

import {ToastrService} from 'ngx-toastr';

import {EMPTY, from, merge, Observable, of, Subject, timer} from 'rxjs';
import {catchError, debounceTime, filter, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {FieldZone, ReportingFields, SelectedTreeItem} from '../../+reports/reports.interfaces';
import {ReportsService} from '../../+reports/reports.service';
import {Destroyable} from '../../destroyable.component';
import {FeatureEnum, ReportsFormData} from '../../models/models.interfaces';
import {Data} from '../../shared/api';
import {Template} from '../../shared/documents/documents.interface';
import {SignatureDataEntry} from '../../shared/files/files.model';
import {FieldType} from '../../shared/form-generator/fields/fields.interfaces';
import {FormGeneratorService} from '../../shared/form-generator/form-generator.service';
import {PREVIEW_STATE} from '../../shared/generate-document/generate-document-modal/generate-document-modal.component';
import * as syncfusionTranslations from '../../shared/i18n/syncfusion-translations.json';
import {GPTranslateService} from '../../shared/localization/translate.service';
import {ModalService} from '../../shared/modal';
import {CliCorePropertiesService} from '../../navigation/cli-core-properties.service';
import {CanComponentDeactivate} from '../../shared/routing/can-deactivate-guard';
import {TreeNode} from '../../shared/ui';
import {TreeItem} from '../../shared/ui/tree-multi-select/treeview.component';
import {User, UserService} from '../../shared/user';
import {Files} from '../../shared/utils/files';
import {
  AiGenerateResult,
  ETemplate,
  FieldAssociation,
  FieldReviewRow,
  FieldReviewState,
  IMAGE_FIELD_PREFIX,
  InsertConditionFormData,
  InsertFieldTab,
  InsertType,
  MergeFieldSpec,
  PlaceholderMatch,
  PreservedField,
  ResolvedPlaceholder,
  TreeViewItem
} from '../template-management.interfaces';
import {TemplateManagementService} from '../template-management.service';

import {AiGenerateModalComponent} from './ai-generate-modal/ai-generate-modal.component';
import {EditorFontsService} from './editor-fonts.service';
import {base64Of, fingerprintOfBase64} from './signature-image.util';
import {RolesService} from '../../shared/authentication/roles/roles.service';
import {TemplateConditionsModalComponent} from './template-conditions-modal/template-conditions-modal.component';

const IMAGE_REFERENCE_KEYS = ['imageString', 'img', 'metaFileImageString', 'mtimg'];

@Component({
  selector: 'gp-document-editor',
  templateUrl: './gp-document-editor.component.html',
  styleUrls: ['./gp-document-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GpDocumentEditorComponent extends Destroyable
  implements CanComponentDeactivate, OnInit, OnChanges, OnDestroy, AfterViewInit {
  public static ACCESS_NAME = 'GpDocumentEditor';
  public eTemplateData: ETemplate;

  @Output()
  public editorContentModified: EventEmitter<boolean> = new EventEmitter<boolean>();

  public editorIsDirty = false;
  public loading = false;
  public saving = false;
  public savingAndClosing = false;
  public insertFieldEnabled = false;
  public insertFieldTab: InsertFieldTab = 'fields';
  public insertSignatureEnabled = false;
  public fieldReviewState: FieldReviewState = 'idle';
  public fieldReviewRows: FieldReviewRow[] = [];
  public signaturePlacementImageDataUri: string | null = null;
  public readonly signatureCounter = (fingerprints: string[]): number =>
    this.countSignatureImagesInDocument(fingerprints);
  public mergeFieldsData: TreeViewItem[] = [];
  public mergeFieldsDataTranslated: TreeViewItem[] = [];
  public filterControl = new UntypedFormControl();
  public treeViewFields: FieldsSettingsModel;
  @ViewChild('treeViewObj')
  public treeViewComponent: TreeViewComponent;
  @ViewChild('editorPreview')
  public documentEditorContainerComponent: DocumentEditorContainerComponent;
  @ViewChild('signaturePlacementPreview')
  public signaturePlacementPreview?: ElementRef<HTMLImageElement>;
  public documentChangeCounter = 0;
  public fieldListTreeForInsertFields: Array<TreeNode<TreeItem>> = null;
  public fieldListTreeForInsertConditions: Array<TreeNode<TreeItem>> = null;
  public showHeader = false;
  public showCustomToolBar = true;
  public showPropertiesPane = true;
  public autoSave = false;
  public readonly documentEditorSettings: DocumentEditorSettingsModel = {
    fontFamilies: [
      'Arial', 'Arial Black', 'Calibri', 'Calibri Light', 'Courier New', 'Georgia', 'Impact', 'Times New Roman', 'Verdana'
    ]
  };
  @Input()
  public eTemplatePreviewData: ETemplate = null;
  public formValuesWrapper: ReportsFormData;
  public documentEditorLocale: string;

  private AUTO_SAVE_POLL_DURATION = 60000;
  /** Bounds the search-and-replace loop so a pathological document cannot spin. */
  private readonly MAX_TOKEN_OCCURRENCES = 200;
  private readonly PRESELECTED_MATCHES: PlaceholderMatch[] = ['EXACT', 'LIKELY'];
  private readonly PLACEHOLDER_PATTERN = /\{[A-Z][A-Z0-9_]*}/g;
  private proposals = new Map<string, ResolvedPlaceholder>();
  private fieldReviewCancelled$ = new Subject<void>();
  private insertFieldToolBarItem: CustomToolbarItemModel;
  private insertConditionToolBarItem: CustomToolbarItemModel;
  private insertSignatureToolBarItem: CustomToolbarItemModel;
  private signatureEnabled = false;
  private editorPreviewCreated = false;
  private readonly signaturePlacementPointerMoveListener = (event: PointerEvent): void =>
    this.moveSignaturePlacementPreview(event);
  private NUMERIC_TYPES = [
    FieldType.INTEGER, FieldType.DECIMAL, FieldType.CURRENCY, FieldType.PERCENTAGE, FieldType.PAY_VALUE
  ];
  private CONDITIONS_ENABLED_FOR_TYPES = [
    FieldType.LABEL, FieldType.DATE,
    ...this.NUMERIC_TYPES,
    FieldType.LIST, FieldType.TREE, FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED,
    FieldType.PEOPLE_TREE
  ];

  constructor(private router: Router,
              private cd: ChangeDetectorRef,
              private route: ActivatedRoute,
              private reportsService: ReportsService,
              private gpTranslateService: GPTranslateService,
              private modalService: ModalService,
              private formGeneratorService: FormGeneratorService,
              public userService: UserService,
              private cliCorePropertiesService: CliCorePropertiesService,
              private rolesService: RolesService,
              private toastService: ToastrService,
              private ngZone: NgZone,
              private editorFontsService: EditorFontsService,
              public templateManagementService: TemplateManagementService) {
    super();
  }

  private get routeParam$(): Observable<number> {
    return this.route.params
      .pipe(
        map((params) => {
          return parseInt(params.id, 10);
        })
      );
  }

  public ngOnInit(): void {
    this.ngZone.runOutsideAngular(() =>
      document.addEventListener('pointermove', this.signaturePlacementPointerMoveListener));
    this.cliCorePropertiesService.get()
      .pipe(
        tap((properties) => {
          this.signatureEnabled = (properties.features?.includes(FeatureEnum.E_SIGNATURE) ?? false)
            && this.rolesService.hasRole('SIGNATURE');
          if (this.editorPreviewCreated) {
            this.bindCustomToolBarButtons(this.showCustomToolBar);
          }
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
    this.bindSyncfusionEditorTranslations();
    this.routeParam$
      .pipe(
        filter(Boolean),
        tap(() => {
          this.showHeader = true;
          this.loading = true;
        }),
        switchMap((eTemplateId: number) => this.handleInsertFields(eTemplateId)),
        switchMap(() => this.showHeader ? this.handleAutoSave() : EMPTY),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.gpTranslateService.onLangChange
      .pipe(
        tap(() => this.translateAndBindDataToTreeView()),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public handleAutoSave(): Observable<User> {
    return this.userService.fetchUser()
      .pipe(
        tap((user: User) => {
          this.autoSave = user.preferences?.eTemplateAutoSave;
          if (this.autoSave) {
            this.startAutoSavePolling();
          }
        }));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.eTemplatePreviewData?.currentValue) {
      this.showCustomToolBar = false;
      this.showPropertiesPane = false;
      this.eTemplateData = {...changes.eTemplatePreviewData?.currentValue};
    }
  }

  public ngOnDestroy(): void {
    document.removeEventListener('pointermove', this.signaturePlacementPointerMoveListener);
    this.documentEditorContainerComponent?.destroy();
    this.editorFontsService.unregister();
    super.ngOnDestroy();
  }

  public handleInsertFields(eTemplateId: number): Observable<ETemplate | ReportingFields> {
    return this.templateManagementService.getETemplate(eTemplateId).pipe(
      tap((response) => this.eTemplateData = response),
      tap(() => this.eTemplateData?.status === 'PUBLISHED' ? this.backToList() : null),
      switchMap(() => this.reportsService.getReportingFieldsByCliRegId(this.eTemplateData?.cliRegId)
        .pipe(
          tap((data: ReportingFields) => {
            const reportingFieldsWithLaunchOptions = this.appendLaunchOptionsToReportingFields(data);

            this.prepareMergeFieldsTreeViewData(reportingFieldsWithLaunchOptions);
            this.prepareFieldTreeForInsertFields(reportingFieldsWithLaunchOptions);
            this.prepareFieldTreeForInsertConditions(reportingFieldsWithLaunchOptions);
          }),
          finalize(() => {
            this.loading = false;
            this.cd.markForCheck();
          })
        )
      ),
      catchError(() => {
        this.eTemplateData = null;
        this.loading = false;
        this.editorIsDirty = true;
        this.cd.markForCheck();
        return EMPTY;
      })
    );
  }

  public ngAfterViewInit(): void {
    this.filterControl.valueChanges
      .pipe(
        debounceTime(400),
        tap((value: string) => {
          this.filterMergeFieldsAndBindToTreeView(value?.trim());
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public canDeactivate(): Observable<boolean> {
    if (!this.editorIsDirty) {
      return of(true);
    }
    return from(
      this.modalService.dynamicConfirmModal({
        'title': 'DIALOG.you_have_unsaved_changes',
        'text': 'DIALOG.you_have_unsaved_changes?',
        'confirmButton': 'FORMS.discard_all_changes',
        'cancelButton': 'FORMS.keep_editing'
      })
        .then((choice) => {
          let canDeactivate: boolean;
          if (choice === true) {
            canDeactivate = true;
          } else {
            canDeactivate = false;
          }
          return canDeactivate;
        })
    );
  }

  public onEditorContentChange(): void {
    this.documentChangeCounter++;
    this.updateEditorDirtyFlag();
  }

  public onDocumentChange(): void {
    if (this.documentChangeCounter > 1) {
      this.updateEditorDirtyFlag();
    }
    this.documentChangeCounter++;
  }

  public backToList(): void {
    this.router.navigate(['/templates']);
  }

  public onEditorPreviewCreated(): void {
    const editor = this.documentEditorContainerComponent.documentEditor;

    if (editor.documentEditorSettings) {
      editor.documentEditorSettings.allowDragAndDrop = false;
    }
    this.editorPreviewCreated = true;
    this.bindCustomToolBarButtons(this.showCustomToolBar);

    this.documentEditorContainerComponent.documentEditor.enableContextMenu = false;
    this.editorFontsService.register()
      .pipe(
        catchError(() => of(undefined)),
        tap(() => {
          this.eTemplateData?.value
            ? this.documentEditorContainerComponent.documentEditor.open(this.eTemplateData?.value)
            : this.documentEditorContainerComponent.documentEditor.openBlank();
          this.documentChangeCounter++;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public onToolBarButtonClicked(event: ClickEventArgs): void {
    const clickedButtonItem = event.item;
    if (clickedButtonItem.id === 'InsertField') {
      if (this.insertFieldEnabled) {
        this.closeInsertFieldPanel();
      } else {
        this.openInsertFieldPanel(this.insertFieldTab);
      }
    } else if (clickedButtonItem.id === 'InsertCondition') {
      this.openInsertConditionsModal();
    } else if (clickedButtonItem.id === 'InsertSignature' && this.signatureEnabled) {
      this.toggleInsertSignaturePanel();
    }
  }

  public onMergeFieldNodeSelecting(eventArgs: NodeSelectEventArgs): void {
    const selectedNodeData = eventArgs.nodeData;
    if (selectedNodeData.hasChildren) {
      this.toggleExpandCollapseOfTreeViewNode(selectedNodeData.expanded !== true, [eventArgs.node]);
    } else {
      let nodeId = selectedNodeData.id?.toString().split('@')[0];
      const field: SelectedTreeItem = {
        id: nodeId,
        previousId: '',
        text: selectedNodeData?.text?.toString()
      };

      const selectedField: TreeNode<TreeItem> = this.templateManagementService
        .getSelectedFieldData(this.fieldListTreeForInsertFields, field);
      const fieldPath: string = this.templateManagementService
        .getPathFromReportingFields(this.fieldListTreeForInsertFields, selectedField);

      const fieldType = selectedField?.data?.data?.types[0];
      if (fieldType === 'FILE') {
        nodeId = IMAGE_FIELD_PREFIX + nodeId;
      }
      this.insertFieldIntoEditor(nodeId, fieldPath);
    }
    eventArgs.cancel = true;
  }

  public filterMergeFieldsAndBindToTreeView(filterText: string): void {
    const allFilteredItemIds: Set<string> = new Set<string>();
    const allItemIdPredicates: Predicate[] = [];
    if (filterText === '') {
      this.updateTreeViewDataSource(this.mergeFieldsDataTranslated);
      return;
    }

    const filterTextPredicate = new Predicate('displayText', 'contains', filterText, true);
    const filteredItems = <TreeViewItem[]>new DataManager(this.mergeFieldsDataTranslated)
      .executeLocal(new Query().where(filterTextPredicate));

    if (filteredItems.length > 0) {
      filteredItems
        .filter(item => item.parentID)
        .forEach(filteredItemWithParent => {
          this.collectFilteredItemIdsOfParent(filteredItemWithParent, allFilteredItemIds);
        });

      filteredItems
        .filter(item => item.hasChildren)
        .forEach(filteredItemWithChildren => {
          this.collectFilteredItemIdsOfChildren(filteredItemWithChildren, allFilteredItemIds);
        });

      allFilteredItemIds.forEach(itemId => {
        allItemIdPredicates.push(new Predicate('id', 'equal', itemId, false));
      });
      const finalFilteredMergeFieldsData = <TreeViewItem[]>new DataManager(this.mergeFieldsDataTranslated)
        .executeLocal(new Query().where(Predicate.or(allItemIdPredicates)));

      this.updateTreeViewDataSource(finalFilteredMergeFieldsData);

      setTimeout(() => {
        this.treeViewComponent?.expandAll();
      }, 100);
    } else {
      this.updateTreeViewDataSource([]);
    }
  }

  public saveTemplate(closeAfterSave: boolean): void {
    this.editorIsDirty = false;
    this.saving = !closeAfterSave;
    this.savingAndClosing = closeAfterSave;
    this.saveEditorBlob(closeAfterSave);
  }

  public onChangeAutoSave(target: EventTarget): void {
    this.autoSave = (target as HTMLInputElement).checked;
    this.userService.updateCliUserPreferences({eTemplateAutoSave: this.autoSave})
      .pipe(
        tap(() => {
          if (this.autoSave) {
            this.startAutoSavePolling();
          }
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public saveTheBlob(closeAfterSave: boolean, blob: Blob): FileReader {
    const fileReader = new FileReader();
    fileReader.onload = (): void => {
      this.eTemplateData.value = <string>fileReader.result;
      this.templateManagementService.updateETemplate(this.eTemplateData)
        .pipe(
          tap(() => {
            if (closeAfterSave) {
              this.savingAndClosing = false;
              this.backToList();
            }
          }),
          finalize(() => {
            this.saving = false;
            this.editorIsDirty = false;
            this.cd.markForCheck();
          }),
          takeUntil(this.componentDestroyed$)
        ).subscribe();
    };
    fileReader.readAsDataURL(blob);
    return fileReader;
  }

  private saveEditorBlob(closeAfterSave: boolean): void {
    this.documentEditorContainerComponent?.documentEditor?.saveAsBlob('Docx').then((blob: Blob) => {
      this.saveTheBlob(closeAfterSave, blob);
    });
  }

  private startAutoSavePolling(): void {
    timer(0, this.AUTO_SAVE_POLL_DURATION)
      .pipe(
        tap(() => {
          this.saveEditorBlob(false);
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public handlePreviewTemplate(): void {
    this.editorBlobToBase64()
      .pipe(
        tap((editorBlobAsBase64String: string) => {
          this.openPreviewTemplateModal(editorBlobAsBase64String);
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public openAiGenerateModal(): void {
    this.modalService.openModalWithName('AiGenerateModalComponent')
      .then((modalRef: ComponentRef<AiGenerateModalComponent>) => {
        modalRef.instance.templateName = this.eTemplateData?.name;
        modalRef.instance.templateLanguage = this.eTemplateData?.locale;

        const editor = this.documentEditorContainerComponent?.documentEditor;
        const editorContent = editor?.serialize();
        if (!this.isEditorEmpty(editor, editorContent)) {
          modalRef.instance.existingSfdt = editorContent;
        }

        modalRef.instance.onGenerated = (result: AiGenerateResult) => {
          this.documentEditorContainerComponent.documentEditor.open(result.sfdt);
          this.reportLostFields(result.preservedFields, this.restorePreservedFields(result.preservedFields));
          this.reportLostImages(result.lostImages);
          this.updateEditorDirtyFlag();
          this.proposals.clear();
          this.fieldReviewRows = [];
          this.fieldReviewState = 'idle';
          if (this.eTemplateData?.cliRegId) {
            this.openInsertFieldPanel('placeholders');
            this.matchWithAi(result.sfdt);
          }
          this.cd.detectChanges();
        };
      });
  }

  public openInsertFieldPanel(tab: InsertFieldTab): void {
    this.insertSignatureEnabled = false;
    this.insertFieldEnabled = true;
    this.translateAndBindDataToTreeView();
    this.insertFieldTab = tab;
    this.refreshPlaceholders();
    this.updateToolbarButtonSelection();
    this.resizeEditorAfterPanelChange();
    this.cd.markForCheck();
  }

  public closeInsertFieldPanel(): void {
    this.insertFieldEnabled = false;
    this.resetTreeViewFilterInput();
    this.updateToolbarButtonSelection();
    this.resizeEditorAfterPanelChange();
    this.cd.markForCheck();
  }

  public selectInsertFieldTab(tab: InsertFieldTab): void {
    this.insertFieldTab = tab;
    if (tab === 'placeholders') {
      this.refreshPlaceholders();
    }
  }

  /**
   * Proposals only fill rows the user has not edited, so matching again never undoes manual work. A
   * newer request supersedes an older one, but closing the panel does not: the result waits for it.
   */
  public matchWithAi(sfdt?: string): void {
    const cliRegId = this.eTemplateData?.cliRegId;
    if (!cliRegId) {
      return;
    }
    this.fieldReviewCancelled$.next();
    this.fieldReviewState = 'loading';
    const source = sfdt ?? this.documentEditorContainerComponent?.documentEditor?.serialize();

    this.templateManagementService.resolveAiTemplateFields({sfdt: source, cliRegId})
      .pipe(
        tap((response) => {
          this.proposals = new Map((response?.placeholders ?? []).map((placeholder) => [placeholder.token, placeholder]));
          this.fieldReviewRows = this.fieldReviewRows.filter((row) => row.overridden);
          this.fieldReviewState = 'ready';
          this.refreshPlaceholders();
        }),
        catchError(() => {
          this.fieldReviewState = 'failed';
          return EMPTY;
        }),
        finalize(() => this.cd.markForCheck()),
        takeUntil(merge(this.fieldReviewCancelled$, this.componentDestroyed$))
      ).subscribe();
  }

  /**
   * The document is the source of truth: rows are rebuilt from the placeholders it still contains,
   * keeping any row the user has already touched and filling the rest from the AI proposals. The
   * Placeholders tab only exists while there are rows, so an empty document falls back to Fields.
   */
  public refreshPlaceholders(): void {
    const tokens = this.scanPlaceholderTokens();
    const occurrences = this.countOccurrences(tokens.map((token) => `{${token}}`));
    const previousRows = new Map(this.fieldReviewRows.map((row) => [row.token, row]));
    this.fieldReviewRows = tokens.map((token) => ({
      ...(previousRows.get(token) ?? this.toReviewRow(token, this.proposals.get(token))),
      occurrences: occurrences.get(`{${token}}`) ?? 0
    }));
    if (!this.fieldReviewRows.length) {
      this.insertFieldTab = 'fields';
    }
    this.cd.markForCheck();
  }

  public refreshFromDocument(): void {
    this.refreshPlaceholders();
    if (!this.fieldReviewRows.length) {
      this.toastService.success(this.gpTranslateService.instant('TEMPLATE.ai_review_all_handled'));
    }
  }

  public onReviewRowsChange(rows: FieldReviewRow[]): void {
    this.fieldReviewRows = rows;
  }

  public locatePlaceholder(searchText: string): void {
    this.documentEditorContainerComponent?.documentEditor?.search.find(searchText, 'CaseSensitive');
  }

  public applyFieldReview(rows: FieldReviewRow[]): void {
    const appliedRows = rows.filter((row) => row.selected && row.fields.length > 0);
    const mapping = new Map<string, MergeFieldSpec[]>(appliedRows.map((row) => [row.searchText, row.fields]));
    this.convertToMergeFields(mapping);
    this.updateEditorDirtyFlag();
    this.refreshPlaceholders();
    this.rememberAppliedRows(appliedRows);
  }

  /** Only improves later proposals, so it is fire-and-forget and never disturbs the Apply. */
  private rememberAppliedRows(appliedRows: FieldReviewRow[]): void {
    const cliRegId = this.eTemplateData?.cliRegId;
    if (!cliRegId || !appliedRows.length) {
      return;
    }
    const associations: FieldAssociation[] = appliedRows.flatMap((row) => {
      const fieldNames = row.fields.map((field) => field.fieldName);
      const applied: FieldAssociation = {
        token: row.token,
        fieldNames,
        outcome: row.overridden || !row.source ? 'OVERRIDDEN' : 'ACCEPTED',
        source: row.source ?? 'NONE'
      };
      return this.isChangedMemoryProposal(row, fieldNames)
        ? [applied, {token: row.token, fieldNames: row.proposedFieldNames, outcome: 'REJECTED', source: 'MEMORY'}]
        : [applied];
    });
    this.templateManagementService.saveAiFieldMemory({cliRegId, associations})
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private isChangedMemoryProposal(row: FieldReviewRow, fieldNames: string[]): boolean {
    return row.source === 'MEMORY' && row.overridden && !!row.proposedFieldNames?.length
      && row.proposedFieldNames.join('|') !== fieldNames.join('|');
  }

  /**
   * Each proposal is checked against the user's reporting fields tree before it becomes a row, so a
   * field the user cannot access becomes an unmatched row and its name never reaches the panel.
   */
  private toReviewRow(token: string, proposal: ResolvedPlaceholder | undefined): FieldReviewRow {
    const row: FieldReviewRow = {
      token, searchText: `{${token}}`, match: null, fields: [], selected: false, occurrences: 0, overridden: false
    };
    if (!proposal) {
      return row;
    }
    const fields = proposal.fields?.length
      ? this.validateAgainstReportingFields(proposal.fields.map((field) => field.fieldName))
      : null;
    if (!fields?.length) {
      return {...row, match: 'NONE', reason: proposal.reason ?? 'NO_MATCH'};
    }
    return {
      ...row, match: proposal.match, source: proposal.source ?? 'AIKB', fields,
      proposedFieldNames: fields.map((field) => field.fieldName),
      selected: this.PRESELECTED_MATCHES.includes(proposal.match)
    };
  }

  /** Reading the text needs a select-all, so the user's selection is put back afterwards. */
  private scanPlaceholderTokens(): string[] {
    const documentEditor = this.documentEditorContainerComponent?.documentEditor;
    if (!documentEditor?.selection) {
      return [];
    }
    const {startOffset, endOffset} = documentEditor.selection;
    documentEditor.selection.selectAll();
    const text = documentEditor.selection.text ?? '';
    documentEditor.selection.select(startOffset, endOffset);
    return [...new Set((text.match(this.PLACEHOLDER_PATTERN) ?? []).map((searchText) => searchText.slice(1, -1)))];
  }

  /** Panels can open without a toolbar click, so button states follow the panels, not the click. */
  private updateToolbarButtonSelection(): void {
    document.getElementById('InsertField')?.classList.toggle('e-btn-toggle', this.insertFieldEnabled);
    document.getElementById('InsertSignature')?.classList.toggle('e-btn-toggle', this.insertSignatureEnabled);
  }

  /** Counting moves the selection, so it is put back for a user who may already be typing. */
  private countOccurrences(searchTexts: string[]): Map<string, number> {
    const counts = new Map<string, number>();
    const documentEditor = this.documentEditorContainerComponent?.documentEditor;
    if (!documentEditor?.search || !documentEditor.selection) {
      return counts;
    }
    const {startOffset, endOffset} = documentEditor.selection;
    searchTexts.forEach((searchText) => {
      documentEditor.search.findAll(searchText, 'CaseSensitive');
      counts.set(searchText, documentEditor.search.searchResults.length);
    });
    documentEditor.search.searchResults.clear();
    documentEditor.selection.select(startOffset, endOffset);
    return counts;
  }

  private resizeEditorAfterPanelChange(): void {
    setTimeout(() => {
      this.documentEditorContainerComponent?.resize(100, 100);
    }, 0);
  }

  /**
   * Rebuilds the merge fields that were swapped for sentinels before the template went to the AI.
   * These need no validation against the reporting fields tree — they were already in the document —
   * and a conditional field is restored from its expression, which no tree lookup would find.
   *
   * @returns how many of the preserved fields were found and restored
   */
  public restorePreservedFields(preservedFields: PreservedField[]): number {
    if (!preservedFields?.length) {
      return 0;
    }
    const mapping = new Map<string, MergeFieldSpec[]>();
    preservedFields.forEach((preserved) =>
      mapping.set(preserved.sentinel, [this.toPreservedSpec(preserved.fieldName)]));
    return this.convertToMergeFields(mapping);
  }

  /** The display text is cosmetic, so a field the tree cannot resolve falls back to its own name. */
  private toPreservedSpec(fieldName: string): MergeFieldSpec {
    const treeName = fieldName.startsWith(IMAGE_FIELD_PREFIX)
      ? fieldName.substring(IMAGE_FIELD_PREFIX.length)
      : fieldName;
    const node = this.templateManagementService.findFieldNodeByName(this.fieldListTreeForInsertFields, treeName);
    return {
      fieldName,
      displayPath: node
        ? this.templateManagementService.getPathFromReportingFields(this.fieldListTreeForInsertFields, node)
        : fieldName
    };
  }

  private reportLostFields(preservedFields: PreservedField[], restoredFields: number): void {
    const expectedFields = preservedFields?.length ?? 0;
    if (restoredFields < expectedFields) {
      this.toastService.warning(this.gpTranslateService.instant('TEMPLATE.ai_fields_not_preserved',
        {lost: expectedFields - restoredFields}));
    }
  }

  private reportLostImages(lostImages: number): void {
    if (lostImages > 0) {
      this.toastService.warning(this.gpTranslateService.instant('TEMPLATE.ai_images_not_preserved', {lost: lostImages}));
    }
  }

  /**
   * Replaces each search text with the merge fields mapped to it. Takes plain field specs rather
   * than an AI response so that any producer of such a mapping — reviewed placeholders, restored
   * sentinels — can reuse this pass unchanged.
   *
   * @returns how many occurrences were converted
   */
  public convertToMergeFields(searchTextToFields: Map<string, MergeFieldSpec[]>): number {
    const documentEditor = this.documentEditorContainerComponent?.documentEditor;
    if (!documentEditor || !searchTextToFields?.size) {
      return 0;
    }

    let convertedOccurrences = 0;
    searchTextToFields.forEach((fields, searchText) => {
      convertedOccurrences += this.replaceOccurrences(searchText, fields);
    });
    return convertedOccurrences;
  }

  /**
   * The reporting fields tree is the authority on what may be inserted: it is scoped to this
   * regulation and filtered by the user's field access, whereas the knowledge base the AI draws on
   * is neither. A composite is only correct in full, so one unknown field rejects the whole token.
   */
  private validateAgainstReportingFields(fieldNames: string[]): MergeFieldSpec[] {
    const specs: MergeFieldSpec[] = [];
    for (const fieldName of fieldNames) {
      const node = this.templateManagementService.findFieldNodeByName(this.fieldListTreeForInsertFields, fieldName);
      if (!node || this.isFileTypeField(node)) {
        return null;
      }
      specs.push({
        fieldName: node.value,
        displayPath: this.templateManagementService.getPathFromReportingFields(this.fieldListTreeForInsertFields, node)
      });
    }
    return specs;
  }

  private isFileTypeField(node: TreeNode<TreeItem>): boolean {
    return node.data?.data?.types?.[0] === 'FILE';
  }

  private replaceOccurrences(searchText: string, fields: MergeFieldSpec[]): number {
    const documentEditor = this.documentEditorContainerComponent.documentEditor;
    let replacedOccurrences = 0;

    documentEditor.selection.moveToDocumentStart();
    for (let attempt = 0; attempt < this.MAX_TOKEN_OCCURRENCES; attempt++) {
      documentEditor.search.find(searchText, 'CaseSensitive');
      if (documentEditor.selection?.text !== searchText) {
        break;
      }
      this.insertFields(fields);
      replacedOccurrences++;
    }
    return replacedOccurrences;
  }

  /** insertField replaces the current selection, so the first insert consumes the matched text. */
  private insertFields(fields: MergeFieldSpec[]): void {
    fields.forEach((field, index) => {
      if (index > 0) {
        this.documentEditorContainerComponent.documentEditor.editor.insertText(' ');
      }
      this.insertFieldIntoEditor(field.fieldName, field.displayPath);
    });
  }

  private isEditorEmpty(editor: DocumentEditorContainerComponent['documentEditor'] | undefined, sfdt: string | undefined): boolean {
    if (!sfdt || sfdt === '' || sfdt === '{}') {
      return true;
    }
    if (!editor?.selection) {
      return false;
    }
    // A blank Syncfusion document still serialises to a non-'{}' SFDT; fall back to the actual text.
    editor.selection.selectAll();
    const text = (editor.selection.text ?? '').replace(/\s+/g, '');
    editor.selection.moveToDocumentStart();
    return text.length === 0;
  }

  private openPreviewTemplateModal(editorBlobAsBase64String: string): void {
    this.modalService.openModalWithName('GenerateDocumentModalComponent').then((modalRef: any) => {
      const TemplateArgument = <Template>{...this.eTemplateData, engine: 'SYNCFUSION'};
      modalRef.instance.eTemplateData = this.eTemplateData;
      modalRef.instance.formValuesWrapper = this.formGeneratorService.transformTemplateArguments([{...TemplateArgument}]);
      modalRef.instance.modalTitle = 'DOCUMENTS.preview_document';
      modalRef.instance.currentPreviewState = PREVIEW_STATE.PREVIEW_IN_PDF_FORMAT_WITHOUT_SAVE;
      modalRef.instance.editorBlobAsString = editorBlobAsBase64String;
    });
  }

  private getEditorContentAsBlob(): Observable<Blob> {
    return from(this.documentEditorContainerComponent.documentEditor.saveAsBlob('Docx'));
  }

  private editorBlobToBase64(): Observable<unknown> {
    return this.getEditorContentAsBlob()
      .pipe(
        switchMap((blob: Blob) => {
          return Files.blobToBase64(blob);
        })
      );
  }

  private appendLaunchOptionsToReportingFields(reportingFields: ReportingFields): ReportingFields {
    const launchOptions = this.eTemplateData.arguments
      .filter(argument => argument.arg1 === 'LAUNCH_OPTION')
      .map(argument => {
        return {
          isRetro: false,
          name: argument.transId,
          transIds: [argument.transId],
          types: [argument.type]
        };
      });

    return {...reportingFields, 'launchOptions': launchOptions};
  }

  private prepareMergeFieldsTreeViewData(reportingFields: ReportingFields): void {
    this.mergeFieldsData = [];
    Object.keys(reportingFields).forEach((rootKey: string) => {
      this.createTreeNode(rootKey, [`REPORTS.${rootKey}_fields`], null, true);
      this.createTreeNodesForChildren(rootKey, reportingFields[rootKey]);
    });
  }

  private prepareFieldTreeForInsertFields(reportingFields: ReportingFields): void {
    const filteredReportingFields: ReportingFields = this.filterAllReportingFieldsByType(reportingFields, InsertType.INSERT_FIELDS);
    this.fieldListTreeForInsertFields = this.createFieldTree(filteredReportingFields);
  }

  private prepareFieldTreeForInsertConditions(reportingFields: ReportingFields): void {
    const filteredReportingFields: ReportingFields = this.filterAllReportingFieldsByType(reportingFields, InsertType.INSERT_CONDITIONS);
    this.fieldListTreeForInsertConditions = this.createFieldTree(filteredReportingFields);
  }

  private filterAllReportingFieldsByType(fields: ReportingFields, insertType: string): ReportingFields {
    const tree: ReportingFields = {
      contract: [],
      employee: [],
      entity: [],
      launchOptions: []
    };

    for (const key in fields) {
      if (fields[key]?.length > 0) {
        tree[key] = this.filterSpecificReportingFieldByType(fields[key], insertType);
      }
    }

    return tree;
  }

  private filterSpecificReportingFieldByType(fieldTree: FieldZone[], insertType: string): FieldZone[] {
    const getNodes = (result: FieldZone[], zone: FieldZone): FieldZone[] => {
      switch (insertType) {
        case InsertType.INSERT_CONDITIONS:
          if (this.isSupportedTypeForInsertConditions(zone)) {
            result.push(zone);
            return result;
          }
          break;

        default:
          result.push(zone);
          return result;
      }

      if (Array.isArray(zone.items)) {
        const items: FieldZone[] = zone.items.reduce(getNodes, []);
        if (items.length) {
          result.push({...zone, items});
        }
      }
      return result;
    };

    return fieldTree.reduce(getNodes, []);
  }

  private isSupportedTypeForInsertConditions(zone: FieldZone): boolean {
    if (Array.isArray(zone?.types)) {
      return !!this.CONDITIONS_ENABLED_FOR_TYPES
        .filter(fieldType => fieldType.toString() === zone?.types[0]).length;
    }
    return false;
  }

  private createFieldTree(fields: ReportingFields | Array<TreeNode<TreeItem>>): TreeNode<TreeItem>[] {
    const lookupMap: Map<string, TreeNode<TreeItem>> = new Map<string, TreeNode<TreeItem>>();
    const tree: Array<TreeNode<TreeItem>> = [];

    if (Array.isArray(fields)) {
      this.createFieldTreeNode(fields, lookupMap);
    } else {
      for (const key in fields) {
        if (fields[key].length > 0) {
          this.createFieldRootTreeNode(key, fields, lookupMap);
        }
      }
    }

    for (const item of Array.from(lookupMap.values())) {
      const hasChildren = item.data?.items?.length > 0;
      if (hasChildren) {
        item.children = this.createFieldTree(item.data.items);
        item.data = null;
      } else {
        item.data = this.fieldZoneToTreeItem(item.data);
        item.selectable = true;
      }
      tree.push(item);
    }

    return tree;
  }

  private createFieldRootTreeNode(rootKey, fields, lookupMap): void {
    const data: Data<TreeItem> = fields[rootKey];
    lookupMap.set(rootKey, {
      text: this.gpTranslateService.instant(`REPORTS.${rootKey}_fields`),
      value: rootKey,
      data: {items: data},
      selectable: false,
      visible: true,
      children: []
    });
  }

  private createFieldTreeNode(fields, lookupMap): void {
    fields.forEach((item: Data<TreeItem>) => lookupMap.set(item.name, {
      text: item.transIds[0],
      value: item.name,
      data: item,
      selectable: false,
      visible: true,
      children: []
    }));
  }

  private fieldZoneToTreeItem(itemData: unknown): TreeItem {
    const zone = itemData as FieldZone;

    const item: TreeItem = new TreeItem(zone.transIds[0], zone.name, zone.transIds);
    item.data = {
      types: zone.types,
      metadatas: zone.metadatas
    };

    return item;
  }

  private openInsertConditionsModal(): void {
    this.modalService.openModalWithName('TemplateConditionsModalComponent')
      .then((modelRef: ComponentRef<TemplateConditionsModalComponent>) => {
        modelRef.instance.insertCondition = this.insertConditionIntoEditor.bind(this);
        modelRef.instance.fieldListTree = this.fieldListTreeForInsertConditions;
      });
  }

  public toggleInsertSignaturePanel(): void {
    this.insertSignatureEnabled = !this.insertSignatureEnabled;
    if (this.insertSignatureEnabled) {
      this.insertFieldEnabled = false;
    } else {
      this.signaturePlacementImageDataUri = null;
    }
    this.updateToolbarButtonSelection();
    setTimeout(() => {
      this.documentEditorContainerComponent?.resize(100, 100);
    }, 0);
    this.cd.markForCheck();
  }

  public closeInsertSignaturePanel(): void {
    this.insertSignatureEnabled = false;
    this.signaturePlacementImageDataUri = null;
    this.updateToolbarButtonSelection();
    setTimeout(() => {
      this.documentEditorContainerComponent?.resize(100, 100);
    }, 0);
    this.cd.markForCheck();
  }

  public get signatureData(): SignatureDataEntry[] {
    const template = this.eTemplateData ?? this.eTemplatePreviewData;
    return template?.signatureData ?? [];
  }

  public get signatureCliRegId(): number | undefined {
    const template = this.eTemplateData ?? this.eTemplatePreviewData;
    return template?.cliRegId;
  }

  public persistSignatureDataOnTemplate(signatureData: SignatureDataEntry[]): void {
    if (this.eTemplateData) {
      this.eTemplateData.signatureData = signatureData;
      this.updateEditorDirtyFlag();
    }
    if (this.eTemplatePreviewData) {
      this.eTemplatePreviewData.signatureData = signatureData;
    }
  }

  public startSignaturePlacement(imageDataUri: string): void {
    setTimeout(() => {
      this.signaturePlacementImageDataUri = imageDataUri;
      this.focusEditorWithoutScrolling();
      this.cd.markForCheck();
    });
  }

  /**
   * Syncfusion focuses its hidden input on mousedown without preventScroll, which nudges the viewer when
   * the editor is not focused yet. Focusing it upfront keeps the placement click from scrolling.
   */
  private focusEditorWithoutScrolling(): void {
    const documentEditor = this.documentEditorContainerComponent?.documentEditor;
    const viewerContainer = documentEditor?.documentHelper?.viewerContainer;
    if (!documentEditor || !viewerContainer) {
      return;
    }
    const {scrollTop, scrollLeft} = viewerContainer;
    documentEditor.focusIn();
    viewerContainer.scrollTop = scrollTop;
    viewerContainer.scrollLeft = scrollLeft;
  }

  public placePendingSignature(event: MouseEvent): void {
    const documentEditor = this.documentEditorContainerComponent?.documentEditor;
    if (!this.signaturePlacementImageDataUri
      || !documentEditor
      || !this.isInsideDocumentViewer(event)) {
      return;
    }
    const viewerContainer = documentEditor.documentHelper?.viewerContainer;
    if (!viewerContainer) {
      return;
    }
    const viewerBounds = viewerContainer.getBoundingClientRect();
    const scrollTop = viewerContainer.scrollTop;
    const scrollLeft = viewerContainer.scrollLeft;
    const skipScrollToPosition = documentEditor.documentHelper.skipScrollToPosition;
    documentEditor.documentHelper.skipScrollToPosition = true;
    try {
      documentEditor.selection.select({
        x: event.clientX - viewerBounds.left,
        y: event.clientY - viewerBounds.top
      });
    } finally {
      documentEditor.documentHelper.skipScrollToPosition = skipScrollToPosition;
      viewerContainer.scrollTop = scrollTop;
      viewerContainer.scrollLeft = scrollLeft;
    }
    const imageDataUri = this.signaturePlacementImageDataUri;
    const insertionOffset = documentEditor.selection.startOffset;
    this.signaturePlacementImageDataUri = null;
    this.insertSignatureImage(imageDataUri, insertionOffset);
    this.cd.markForCheck();
  }

  public removeUnsupportedImageMoveCursor(event: MouseEvent): void {
    if (event.target instanceof HTMLElement && event.target.style.cursor === 'move') {
      event.target.style.cursor = 'default';
    }
  }

  public moveSignaturePlacementPreview(event: PointerEvent): void {
    const preview = this.signaturePlacementPreview?.nativeElement;
    if (!this.signaturePlacementImageDataUri || !preview || event.pointerType === 'touch') {
      return;
    }
    preview.style.transform = `translate3d(${event.clientX + 16}px, ${event.clientY + 16}px, 0)`;
    preview.classList.add('signature-placement-preview--visible');
  }

  @HostListener('document:keydown.escape')
  public cancelSignaturePlacement(): void {
    if (!this.signaturePlacementImageDataUri) {
      return;
    }
    this.signaturePlacementImageDataUri = null;
    this.cd.markForCheck();
  }

  @HostListener('document:mousedown', ['$event'])
  public cancelSignaturePlacementOutsideDocument(event: MouseEvent): void {
    if (this.signaturePlacementImageDataUri
      && !this.isInsideDocumentViewer(event)) {
      this.cancelSignaturePlacement();
    }
  }

  private isInsideDocumentViewer(event: MouseEvent): boolean {
    const viewerContainer = this.documentEditorContainerComponent?.documentEditor
      ?.documentHelper?.viewerContainer;
    if (!viewerContainer) {
      return event.target instanceof Element && !!event.target.closest('.e-de-ctn');
    }
    const bounds = viewerContainer.getBoundingClientRect();
    return event.clientX >= bounds.left && event.clientX <= bounds.right
      && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
  }

  public insertSignatureImage(imageDataUri: string, insertionOffset?: string): void {
    const img = new Image();
    img.onload = (): void => {
      const documentEditor = this.documentEditorContainerComponent?.documentEditor;
      const documentHelper = documentEditor?.documentHelper;
      const viewerContainer = documentHelper?.viewerContainer;
      if (!documentEditor || !documentHelper || !viewerContainer) {
        return;
      }
      const scrollTop = viewerContainer.scrollTop;
      const scrollLeft = viewerContainer.scrollLeft;
      const skipScrollToPosition = documentHelper.skipScrollToPosition;
      documentHelper.skipScrollToPosition = true;
      try {
        if (insertionOffset) {
          documentEditor.selection.select(insertionOffset, insertionOffset);
          documentHelper.skipScrollToPosition = true;
        }
        documentEditor.editor?.insertImage(imageDataUri, img.naturalWidth, img.naturalHeight);
        documentEditor.focusIn();
      } finally {
        documentHelper.skipScrollToPosition = skipScrollToPosition;
        viewerContainer.scrollTop = scrollTop;
        viewerContainer.scrollLeft = scrollLeft;
      }
      this.updateEditorDirtyFlag();
    };
    img.src = imageDataUri;
  }

  public removeSignatureImagesFromDocument(fingerprints: string[]): void {
    const documentEditor = this.documentEditorContainerComponent?.documentEditor;
    const serialized = documentEditor?.serialize();
    if (!serialized || !fingerprints?.length) {
      return;
    }
    try {
      const sfdt = JSON.parse(serialized);
      const matchedImageIds = this.matchImageIds(sfdt, fingerprints);
      this.removeMatchingImageElements(sfdt, matchedImageIds, fingerprints);
      this.compactImageDictionary(sfdt);
      documentEditor.open(JSON.stringify(sfdt));
      this.documentChangeCounter++;
      this.updateEditorDirtyFlag();
    } catch {
      return;
    }
  }

  public countSignatureImagesInDocument(fingerprints: string[]): number {
    const serialized = this.documentEditorContainerComponent?.documentEditor?.serialize();
    if (!serialized || !fingerprints?.length) {
      return 0;
    }
    try {
      const sfdt = JSON.parse(serialized);
      const matchedImageIds = this.matchImageIds(sfdt, fingerprints);
      return this.countMatchingImageElements(sfdt, matchedImageIds, fingerprints);
    } catch {
      return 0;
    }
  }

  private imageDictionaryKeyOf(sfdt: Record<string, unknown>): string | null {
    return sfdt?.['images'] ? 'images' : (sfdt?.['imgs'] ? 'imgs' : null);
  }

  /**
   * Syncfusion stores every dictionary entry as [imageString] or [metaFileImageString, imageString],
   * while older non optimized documents keep a plain base64 string.
   */
  private imageDictionaryValues(imageValue: unknown): string[] {
    if (Array.isArray(imageValue)) {
      return imageValue.filter((value): value is string => typeof value === 'string');
    }
    return typeof imageValue === 'string' ? [imageValue] : [];
  }

  private matchesFingerprint(base64Candidate: string, fingerprints: string[]): boolean {
    const fingerprint = fingerprintOfBase64(base64Of(base64Candidate));
    return !!fingerprint && fingerprints.includes(fingerprint);
  }

  private matchImageIds(sfdt: Record<string, unknown>, fingerprints: string[]): Set<string> {
    const matchedImageIds = new Set<string>();
    const imageDictionaryKey = this.imageDictionaryKeyOf(sfdt);
    if (!imageDictionaryKey) {
      return matchedImageIds;
    }
    Object.entries(sfdt[imageDictionaryKey] as Record<string, unknown>).forEach(([imageId, imageValue]) => {
      if (this.imageDictionaryValues(imageValue).some((value) => this.matchesFingerprint(value, fingerprints))) {
        matchedImageIds.add(imageId);
      }
    });
    return matchedImageIds;
  }

  /**
   * Syncfusion allocates the id of a newly inserted image from the size of the dictionary, so leaving
   * gaps behind would make the next insertion collide with a still existing entry.
   */
  private compactImageDictionary(sfdt: Record<string, unknown>): void {
    const imageDictionaryKey = this.imageDictionaryKeyOf(sfdt);
    if (!imageDictionaryKey) {
      return;
    }
    const imageDictionary = sfdt[imageDictionaryKey] as Record<string, unknown>;
    const referencedImageIds = new Set<string>();
    this.collectReferencedImageIds(sfdt, referencedImageIds);

    const renumberedImageIds = new Map<string, string>();
    const compactedDictionary: Record<string, unknown> = {};
    Object.keys(imageDictionary)
      .filter((imageId) => referencedImageIds.has(imageId))
      .forEach((imageId) => {
        const newImageId = String(renumberedImageIds.size);
        renumberedImageIds.set(imageId, newImageId);
        compactedDictionary[newImageId] = imageDictionary[imageId];
      });

    sfdt[imageDictionaryKey] = compactedDictionary;
    this.renumberImageReferences(sfdt, renumberedImageIds);
  }

  private collectReferencedImageIds(node: unknown, imageIds: Set<string>): void {
    this.forEachImageReference(node, (value: string) => {
      imageIds.add(value);
      return value;
    });
  }

  private renumberImageReferences(node: unknown, renumberedImageIds: Map<string, string>): void {
    this.forEachImageReference(node, (value: string) => renumberedImageIds.get(value) ?? value);
  }

  private forEachImageReference(node: unknown, visit: (value: string) => string): void {
    if (Array.isArray(node)) {
      node.forEach((item) => this.forEachImageReference(item, visit));
      return;
    }
    if (node === null || typeof node !== 'object') {
      return;
    }
    const record = node as Record<string, unknown>;
    Object.entries(record).forEach(([key, value]) => {
      if (IMAGE_REFERENCE_KEYS.includes(key) && (typeof value === 'string' || typeof value === 'number')) {
        record[key] = visit(String(value));
      } else {
        this.forEachImageReference(value, visit);
      }
    });
  }

  private countMatchingImageElements(node: unknown, matchedImageIds: Set<string>, fingerprints: string[]): number {
    if (Array.isArray(node)) {
      return node.reduce((count: number, item: unknown) => count
        + (this.isMatchingImageElement(item, matchedImageIds, fingerprints)
          ? 1
          : this.countMatchingImageElements(item, matchedImageIds, fingerprints)), 0);
    }
    if (node === null || typeof node !== 'object') {
      return 0;
    }
    return Object.values(node as Record<string, unknown>)
      .reduce<number>((count: number, value: unknown) =>
        count + this.countMatchingImageElements(value, matchedImageIds, fingerprints), 0);
  }

  private removeMatchingImageElements(node: unknown, matchedImageIds: Set<string>, fingerprints: string[]): void {
    if (Array.isArray(node)) {
      for (let index = node.length - 1; index >= 0; index--) {
        if (this.isMatchingImageElement(node[index], matchedImageIds, fingerprints)) {
          node.splice(index, 1);
        } else {
          this.removeMatchingImageElements(node[index], matchedImageIds, fingerprints);
        }
      }
      return;
    }
    if (node === null || typeof node !== 'object') {
      return;
    }
    Object.values(node as Record<string, unknown>)
      .forEach((value) => this.removeMatchingImageElements(value, matchedImageIds, fingerprints));
  }

  private isMatchingImageElement(node: unknown, matchedImageIds: Set<string>, fingerprints: string[]): boolean {
    if (node === null || typeof node !== 'object') {
      return false;
    }
    return IMAGE_REFERENCE_KEYS.some((key) => {
      const value = (node as Record<string, unknown>)[key];
      if (typeof value === 'number') {
        return matchedImageIds.has(String(value));
      }
      if (typeof value !== 'string') {
        return false;
      }
      return matchedImageIds.has(value) || this.matchesFingerprint(value, fingerprints);
    });
  }


  private bindCustomToolBarButtons(includeCustomToolBars = true): void {
    const defaultToolBarItems = cloneDeep(this.documentEditorContainerComponent.toolbarItems);
    if (includeCustomToolBars) {
      this.insertFieldToolBarItem = this.getToolBarButtonItem('InsertField', this.gpTranslateService.instant('TEMPLATE.insert_field'), 'e-add-edit-form-field');
      this.insertConditionToolBarItem = this.getToolBarButtonItem('InsertCondition', this.gpTranslateService.instant('TEMPLATE.insert_conditions'), 'e-add-edit-form-field');
      if (this.signatureEnabled) {
        this.addInsertSignatureToolbarButton();
        defaultToolBarItems.splice(5, 0, 'Separator', this.insertFieldToolBarItem, 'Separator', this.insertConditionToolBarItem, 'Separator', this.insertSignatureToolBarItem, 'Separator');
      } else {
        defaultToolBarItems.splice(5, 0, 'Separator', this.insertFieldToolBarItem, 'Separator', this.insertConditionToolBarItem, 'Separator');
      }
    }
    this.documentEditorContainerComponent.toolbarItems = defaultToolBarItems;
  }

  private addInsertSignatureToolbarButton(): void {
    this.insertSignatureToolBarItem = this.getToolBarButtonItem(
      'InsertSignature',
      this.gpTranslateService.instant('TEMPLATE.insert_signature'),
      'fa fa-light fa-signature'
    );
  }

  private collectFilteredItemIdsOfParent(filteredItem: TreeViewItem,
                                         filteredItemIds: Set<string>): void {
    filteredItemIds.add(filteredItem['id']);

    const query = new Query().where('id', 'equal', filteredItem['parentID'], false);
    const filteredParentItems = <TreeViewItem[]>new DataManager(this.mergeFieldsDataTranslated)
      .executeLocal(query);

    if (filteredParentItems.length > 0) {
      this.collectFilteredItemIdsOfParent(filteredParentItems[0], filteredItemIds);
    }
  }

  private collectFilteredItemIdsOfChildren(filteredItem: TreeViewItem,
                                           filteredItemIds: Set<string>): void {
    filteredItemIds.add(filteredItem['id']);

    const query = new Query().where('parentID', 'equal', filteredItem['id'], false);
    const filteredChildItems = <TreeViewItem[]>new DataManager(this.mergeFieldsDataTranslated)
      .executeLocal(query);

    if (filteredChildItems.length > 0) {
      filteredChildItems.forEach(childItem => {
        this.collectFilteredItemIdsOfChildren(childItem, filteredItemIds);
      });
    }
  }


  private createTreeNodesForChildren(parentID: string, children: FieldZone[]): void {
    children.forEach((element: FieldZone) => {
      const itemID = element.name + '@' + parentID;
      if (element.items) {
        this.createTreeNode(itemID, element.transIds, parentID, true);
        this.createTreeNodesForChildren(itemID, element.items);
      } else {
        this.createTreeNode(itemID, element.transIds, parentID, false);
      }
    });
  }

  private createTreeNode(id: string, transIds: string[], parentID?: string, hasChildren?: boolean): void {
    const nodeItem: TreeViewItem = {id, transIds, parentID, hasChildren};
    this.mergeFieldsData.push(nodeItem);
  }

  private getToolBarButtonItem(id: string, displayText: string, prefixIcon: string): CustomToolbarItemModel {
    return {
      prefixIcon: prefixIcon,
      tooltipText: displayText,
      text: this.wrapText(displayText),
      id: id
    };
  }

  private wrapText(text: string): string {
    const wrapIndex: number = text.lastIndexOf(' ');
    let wrappedText = text.slice(0, wrapIndex);
    wrappedText += '<div class="e-de-text-wrap">' + text.slice(wrapIndex) + '</div>';
    return wrappedText;
  }

  private translateAndBindDataToTreeView(): void {
    if (this.insertFieldEnabled) {
      this.mergeFieldsDataTranslated = cloneDeep(this.mergeFieldsData);

      this.mergeFieldsDataTranslated.map((item: TreeViewItem) => {
        item.displayText = this.transIdsDisplay(item.transIds);
      });

      this.mergeFieldsDataTranslated.sort((a: TreeViewItem, b: TreeViewItem) => a.displayText.localeCompare(b.displayText));

      this.updateTreeViewDataSource(this.mergeFieldsDataTranslated);
    }
  }

  private updateTreeViewDataSource(data): void {
    this.treeViewFields = {
      dataSource: data,
      id: 'id',
      text: 'displayText',
      parentID: 'parentID',
      hasChildren: 'hasChildren'
    };
  }

  private toggleExpandCollapseOfTreeViewNode(isExpand: boolean, nodes: HTMLLIElement[]): void {
    if (isExpand) {
      this.treeViewComponent.expandAll(nodes);
    } else {
      this.treeViewComponent.collapseAll(nodes);
    }
  }

  public insertFieldIntoEditor(nodeId: string, fieldName: string): void {
    const mergeFieldCode = 'MERGEFIELD  ' + nodeId + '  \\* MERGEFORMAT ';
    this.documentEditorContainerComponent?.documentEditor?.editor?.insertField(mergeFieldCode, '«' + fieldName + '»');
    this.documentEditorContainerComponent?.documentEditor?.focusIn();
  }

  private insertConditionIntoEditor({
                                      fieldName,
                                      operator,
                                      valueToCompare,
                                      trueResult,
                                      falseResult
                                    }: InsertConditionFormData, operatorSymbol: string, fieldPath: string): void {
    const separator = '§';
    valueToCompare = valueToCompare || '';
    operatorSymbol = operatorSymbol.includes('.') ? operatorSymbol.split('.')[1].replace(/_/g, ' ') : operatorSymbol;

    const ifConditionCode = [
      'MERGEFIELD  IF',
      separator, fieldName, separator, operator, separator, valueToCompare,
      separator, trueResult, separator, falseResult,
      '  \\* MERGEFORMAT '
    ].join('');

    const ifConditionDisplay: string[] = [
      'if (', fieldPath, ') ', operatorSymbol
    ];

    if (valueToCompare.length > 0) {
      ifConditionDisplay.push(' ', `'${valueToCompare}'`, ' ');
    } else {
      ifConditionDisplay.push(', ');
    }

    ifConditionDisplay.push('then ', `'${trueResult}'`);

    if (falseResult?.length > 0) {
      ifConditionDisplay.push(' else ', `'${falseResult}'`);
    }

    this.documentEditorContainerComponent.documentEditor.editor.insertField(ifConditionCode, '«' + ifConditionDisplay.join('') + '»');
    this.documentEditorContainerComponent.documentEditor.focusIn();
  }

  private resetTreeViewFilterInput(): void {
    this.filterControl.setValue('');
  }

  private transIdsDisplay(transIds: string[]): string {
    return this.gpTranslateService.getDisplayTranslation(transIds);
  }

  private updateEditorDirtyFlag(): void {
    this.editorIsDirty = true;
    this.editorContentModified.emit(true);
    this.cd.markForCheck();
  }

  private bindSyncfusionEditorTranslations(): void {
    const locale = this.gpTranslateService.currentLang.split('-')[0];
    if (this.documentEditorLocale !== locale) {
      this.documentEditorLocale = locale;
      L10n.load({[this.documentEditorLocale]: syncfusionTranslations[this.documentEditorLocale]});
      if (this.insertFieldToolBarItem && this.insertConditionToolBarItem) {
        this.insertFieldToolBarItem.text = this.wrapText(this.gpTranslateService.instant('TEMPLATE.insert_field'));
        this.insertConditionToolBarItem.text = this.wrapText(this.gpTranslateService.instant('TEMPLATE.insert_conditions'));
        if (this.insertSignatureToolBarItem) {
          this.insertSignatureToolBarItem.text = this.wrapText(this.gpTranslateService.instant('TEMPLATE.insert_signature'));
        }
      }
    }
  }

  public handleFileExport(fileName: string): void {
    this.documentEditorContainerComponent.documentEditor.saveAsBlob('Docx')
      .then((blob: Blob) => {
        fileName = fileName.replace(/ /g, '_');
        Files.downloadBlob(blob, `${fileName}.docx`);
      });
  }
}
